import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import SEOHead from '@/components/base/SEOHead';
import CategoryNav from './components/CategoryNav';
import WelfareCardGrid from './components/WelfareCardGrid';
import ArticleSection from './components/ArticleSection';
import FAQSection from './components/FAQSection';
import AboutBrief from './components/AboutBrief';
import { welfareItems, siteConfig } from '@/mocks/welfare';

const hotItems = welfareItems.filter((item) => item.hot).slice(0, 8);
const latestItems = welfareItems.slice(0, 8);

export default function Home() {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        name: siteConfig.name,
        url: siteConfig.url,
        description: siteConfig.description,
        email: siteConfig.email,
        telephone: siteConfig.phone,
        address: {
          '@type': 'PostalAddress',
          addressLocality: '北京',
          addressRegion: '海淀区',
          addressCountry: 'CN',
        },
      },
      {
        '@type': 'WebSite',
        name: siteConfig.name,
        url: siteConfig.url,
        description: siteConfig.description,
        potentialAction: {
          '@type': 'SearchAction',
          target: `${siteConfig.url}/search?q={search_term_string}`,
          'query-input': 'required name=search_term_string',
        },
      },
      {
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: '福利导航网是免费的吗？',
            acceptedAnswer: {
              '@type': 'Answer',
              text: '是的，福利导航网完全免费！我们致力于为用户聚合全网最优质的福利资源，不向用户收取任何费用。',
            },
          },
          {
            '@type': 'Question',
            name: '优惠券领取后如何使用？',
            acceptedAnswer: {
              '@type': 'Answer',
              text: '找到心仪的优惠券后，点击立即领取按钮，系统会自动跳转到对应平台领取页面。领取成功后在下单时选择使用即可。',
            },
          },
        ],
      },
    ],
  };

  return (
    <>
      <SEOHead
        title="福利导航网 - 一站式福利资源聚合平台"
        description={siteConfig.description}
        keywords={siteConfig.keywords}
        canonicalUrl={siteConfig.url}
        jsonLd={jsonLd}
      />
      <ScrollToTop />
      <Navbar />

      <main>
        <section className="relative min-h-[580px] md:min-h-[680px] flex items-center justify-center overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Warm%20abstract%20geometric%20background%20with%20soft%20orange%20and%20cream%20gradients%2C%20modern%20minimal%20aesthetic%2C%20gentle%20flowing%20curves%2C%20warm%20atmospheric%20lighting%2C%20clean%20and%20airy%20composition%2C%20suitable%20for%20website%20hero%20background&width=1600&height=900&seq=hero-welfare-2026&orientation=landscape"
            alt=""
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background-950/40 via-background-950/25 to-background-950/50"></div>
          <div className="relative z-10 w-full max-w-4xl mx-auto px-4 md:px-6 text-center">
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold font-heading text-white mb-4 leading-tight">
              一站式福利资源聚合平台
            </h1>
            <p className="text-base md:text-lg text-background-200 mb-8 max-w-2xl mx-auto leading-relaxed">
              汇集海量优惠券、折扣活动、会员福利、返利信息及限时特惠，帮助您快速找到最新最全的福利资源，省钱省时更省心
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/category/all"
                className="w-full sm:w-auto px-8 py-3.5 bg-primary-500 text-white rounded-lg text-base font-medium hover:bg-primary-600 transition-all hover:scale-105 whitespace-nowrap"
              >
                浏览全部福利 <i className="ri-arrow-right-line ml-1"></i>
              </Link>
              <Link
                to="/about"
                className="w-full sm:w-auto px-8 py-3.5 bg-white/15 backdrop-blur-sm text-white border border-white/25 rounded-lg text-base font-medium hover:bg-white/25 transition-all hover:scale-105 whitespace-nowrap"
              >
                了解更多 <i className="ri-information-line ml-1"></i>
              </Link>
            </div>
            <div className="mt-12 flex items-center justify-center gap-8 md:gap-12">
              {[
                { value: '500,000+', label: '累计用户' },
                { value: '10,000+', label: '福利资源' },
                { value: '200+', label: '合作平台' },
              ].map((s) => (
                <div key={s.label} className="text-center">
                  <div className="text-2xl md:text-3xl font-bold text-white font-heading">{s.value}</div>
                  <div className="text-xs md:text-sm text-background-300 mt-1">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <CategoryNav />

        <section className="py-14 md:py-16 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-end justify-between mb-10">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-3">热门福利推荐</h2>
                <p className="text-foreground-500 text-sm md:text-base">大家都在领的优质福利</p>
              </div>
              <Link
                to="/category/all"
                className="hidden sm:flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap"
              >
                查看全部 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div data-product-shop>
              <WelfareCardGrid items={hotItems} />
            </div>
          </div>
        </section>

        <section className="py-14 md:py-16">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-end justify-between mb-10">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-3">最新福利资源</h2>
                <p className="text-foreground-500 text-sm md:text-base">实时更新的福利信息，不错过任何优惠</p>
              </div>
              <Link
                to="/category/all"
                className="hidden sm:flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap"
              >
                查看全部 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div data-product-shop>
              <WelfareCardGrid items={latestItems} />
            </div>
          </div>
        </section>

        <ArticleSection />
        <FAQSection />
        <AboutBrief />
      </main>

      <Footer />
    </>
  );
}